import { DestroyRef, Injectable, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  finalize,
  firstValueFrom,
  forkJoin,
  map,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import {
  readDateOfJoiningFromUserDetailsCookie,
  resolveTimesheetFillMinDateStr,
} from '../utils/timesheet-fill-min-date.util';
import {
  formatCompactDurationFromHours,
  formatDenseDurationFromHours,
  sumHoursTruncatedToMinutes,
  wholeMinutesFromHours,
} from '../utils/hhmm-duration.util';
import {
  ApprovalHistoryEvent,
  AssignedTask,
  DailyProjectGroup,
  MonthDayProjectDetail,
  MonthDayProjectTaskDetail,
  StatCard,
  TimesheetEmployeeProfile,
  TimesheetActivityTaskLine,
  TimesheetActivityType,
  TimesheetEntry,
  timesheetAppDisplayName,
  UnreportedActivity,
  ViewMode,
  WeekDay,
  WeeklyProjectGroup,
  WeeklyTaskGroup,
  WeekStatus,
} from '../models/timesheet.models';
import {
  TimesheetService as TimesheetManagementApiService,
  normalizeProjectTimesheetListItems,
  type ProjectTimesheetDataActivity,
  type ProjectTimesheetDataProjectDay,
  type ProjectTimesheetListItem,
  type ProjectTimesheetUnreportedEntriesResponse,
  type ProjectTimesheetUnreportedEntry,
  type ProjectTimesheetTimesheetDataResponse,
} from '../../../services/timesheet-management/timesheet-service';
import { ProjectManagementService } from '../../../services/project-management/project-management-service';
import { TsDailyViewComponent } from '../components/views/daily-view/ts-daily-view.component';
import { ActivityAppsService } from './activity-apps.service';
import type { MyTimesheetDeepLinkParams } from '../utils/my-timesheet-deep-link.util';

/** Only when API sends `app_id` — do not infer app from `project_type` (manual rows stay unbadged). */
function activityAppBadgeLabel(act: { app_id: number | null }): string | undefined {
  if (act.app_id != null) return timesheetAppDisplayName(act.app_id);
  return undefined;
}

function normalizeActivityType(raw: string | undefined | null): TimesheetActivityType | null {
  const t = (raw || '').toLowerCase();
  if (t === 'manual' || t === 'tracked' || t === 'untracked') return t;
  return null;
}

function activityTitle(type: TimesheetActivityType | null): string {
  if (type === 'manual') return 'Manual';
  if (type === 'tracked') return 'Tracked Activity';
  if (type === 'untracked') return 'Untracked Activity';
  return 'Activity';
}

const API_ENTRY_ID_PREFIX = 'ts-api-';

type DeleteMappedActivityTasksPayload = {
  taskIds: number[];
  date: string;
  sourceType?: string;
};

const PROJECT_DAY_SEVERITY: Record<WeekStatus, number> = {
  rejected: 0,
  submitted: 1,
  revoked: 2,
  draft: 2,
  approved: 3,
};

function pickStricterProjectDayStatus(a: WeekStatus, b: WeekStatus): WeekStatus {
  return PROJECT_DAY_SEVERITY[a] <= PROJECT_DAY_SEVERITY[b] ? a : b;
}

/**
 * Rolls up per (date × CRM project) statuses across a week.
 * Falls back to clock-week `weekStatus` map when there are no project-day cells.
 */
function aggregateWeekStatusFromEntries(
  entries: TimesheetEntry[],
  weekDateStrs: string[],
  fallbackWeekStatus: WeekStatus,
): WeekStatus {
  const dateSet = new Set(weekDateStrs);
  const byCell = new Map<string, WeekStatus>();
  for (const e of entries) {
    if (!dateSet.has(e.date) || e.timesheetProjectId == null) continue;
    const st: WeekStatus = e.projectDayStatus ?? 'draft';
    const k = `${e.date}|${e.timesheetProjectId}`;
    const prev = byCell.get(k);
    byCell.set(k, prev == null ? st : pickStricterProjectDayStatus(prev, st));
  }
  if (byCell.size === 0) return fallbackWeekStatus;
  const cells = [...byCell.values()];
  if (cells.some((s) => s === 'rejected')) return 'rejected';
  if (cells.some((s) => s === 'submitted')) return 'submitted';
  if (cells.some((s) => s === 'approved')) return 'approved';
  if (cells.some((s) => s === 'draft' || s === 'revoked')) return 'draft';
  return 'draft';
}

/** Rolls up per-day project status across one weekly matrix group (matches week-level precedence). */
/** `dateStr` is within Mon–Sun `weekDays` (inclusive, `YYYY-MM-DD`). */
function isDateWithinWeekRange(dateStr: string, weekStartStr: string, weekEndStr: string): boolean {
  return dateStr >= weekStartStr && dateStr <= weekEndStr;
}

/**
 * Submit / resubmit timing for the week shown in the topbar (Mon–Sun):
 * - **Current calendar week** (today is Mon–Sun of that week): **Saturday and Sunday only** (Mon–Fri blocked).
 * - **Past week** (today is after that week's Sunday): allowed (includes resubmit after rejection).
 * - **Future week**: never.
 */
function hasWeekReachedSubmitEligibility(
  weekDays: Date[],
  today: Date,
  dateToString: (d: Date) => string,
): boolean {
  if (weekDays.length < 7) return false;
  const todayStr = dateToString(today);
  const weekStartStr = dateToString(weekDays[0]!);
  const weekEndStr = dateToString(weekDays[6]!);
  const saturdayStr = dateToString(weekDays[5]!);
  if (isDateWithinWeekRange(todayStr, weekStartStr, weekEndStr)) {
    return todayStr >= saturdayStr && todayStr <= weekEndStr;
  }
  return todayStr > weekEndStr;
}

function projectGroupLoggedHours(pg: WeeklyProjectGroup): number {
  let visible = 0;
  for (const t of pg.tasks) {
    for (const list of Object.values(t.entriesByDate)) {
      for (const e of list) {
        visible += e.hrs;
      }
    }
  }
  return visible + (pg.suppressedLoggedHrs ?? 0);
}

function rollupProjectWeekStatusFromWeeklyTasks(tasks: WeeklyTaskGroup[]): WeekStatus {
  const byCell = new Map<string, WeekStatus>();
  for (const tg of tasks) {
    for (const list of Object.values(tg.entriesByDate)) {
      for (const e of list) {
        const pid = e.timesheetProjectId ?? 0;
        const key = `${e.date}|${pid}`;
        const st: WeekStatus = e.projectDayStatus ?? 'draft';
        const prev = byCell.get(key);
        byCell.set(key, prev == null ? st : pickStricterProjectDayStatus(prev, st));
      }
    }
  }
  if (byCell.size === 0) return 'draft';
  const cells = [...byCell.values()];
  if (cells.some((s) => s === 'rejected')) return 'rejected';
  if (cells.some((s) => s === 'submitted')) return 'submitted';
  if (cells.some((s) => s === 'approved')) return 'approved';
  if (cells.some((s) => s === 'draft' || s === 'revoked')) return 'draft';
  return 'draft';
}

const GENERIC_DISPLAY_TASK_TITLES = new Set([
  'Manual',
  'Tracked Activity',
  'Untracked Activity',
  'Activity',
]);

/** Matches weekly `taskRowDisplayName` !== '—' for API rows (mock rows always shown). */
function weeklyTaskRowHasDisplayLabel(task: WeeklyTaskGroup, sample: TimesheetEntry): boolean {
  if (!sample.id.startsWith(API_ENTRY_ID_PREFIX)) return true;
  const pick = (s: string | undefined) => {
    const t = (s ?? '').trim();
    return t && !GENERIC_DISPLAY_TASK_TITLES.has(t) ? t : '';
  };
  const line = sample.activityTaskLines?.[0];
  if (pick(line?.taskName) || pick(task.taskName)) return true;
  const tid = line?.taskId;
  return tid != null && Number.isFinite(tid) && tid > 0;
}

function firstEntryInWeeklyTaskGroup(t: WeeklyTaskGroup): TimesheetEntry | undefined {
  for (const list of Object.values(t.entriesByDate)) {
    if (list.length) return list[0];
  }
  return undefined;
}

/** API rows that share activity identity roll into one activity block in daily view. */
function dailyActivityGroupKey(e: TimesheetEntry): string | null {
  if (!e.id.startsWith(API_ENTRY_ID_PREFIX) || !e.activityType) return null;
  const pid = e.timesheetProjectId ?? 0;
  const aid = e.timesheetActivityId;
  if (aid != null && aid > 0) {
    return `a|${pid}|${aid}|${e.activityType}`;
  }
  const ix = e.activityIndex;
  if (ix != null && ix >= 0) {
    return `a|${pid}|d|${e.date}|i|${ix}|${e.activityType}`;
  }
  return null;
}

/** Roll up by CRM task identity + activity type (manual / tracked / untracked). */
function taskRollupGroupingKey(e: TimesheetEntry): string {
  if (e.id.startsWith(API_ENTRY_ID_PREFIX)) {
    const pid = e.timesheetProjectId ?? 0;
    const at = e.activityType ?? 'na';
    const line = e.activityTaskLines?.[0];
    const tid = line?.taskId;
    if (tid != null && Number.isFinite(tid) && tid > 0) {
      return `api|${pid}|tid|${tid}|at|${at}`;
    }
    const st = (line?.stageName ?? e.stage ?? '').trim();
    const ms = (line?.milestoneName ?? e.ms ?? '').trim();
    const tn = (line?.taskName ?? e.task ?? '').trim();
    if (st || ms || tn) {
      return `api|${pid}|crm|${st}|${ms}|${tn}|at|${at}`;
    }
    const aid = e.timesheetActivityId;
    if (aid != null && aid > 0) {
      return `api|${pid}|act|${at}|aid|${aid}`;
    }
    const app = (e.activityAppLabel ?? '').trim();
    return `api|${pid}|act|${at}|app|${app}`;
  }
  return `${e.stage}|${e.ms}|${e.task}`;
}

function sortEntriesSharingActivity(a: TimesheetEntry, b: TimesheetEntry): number {
  const ta = a.activityTaskLines?.[0]?.taskId ?? 0;
  const tb = b.activityTaskLines?.[0]?.taskId ?? 0;
  if (ta !== tb) return ta - tb;
  return a.id.localeCompare(b.id);
}

const PROJECT_COLOR_PALETTE = [
  '#3730A3',
  '#5B21B6',
  '#065F46',
  '#B45309',
  '#0E7490',
  '#BE185D',
  '#1D4ED8',
  '#047857',
] as const;

function colorForProjectId(projectId: number): string {
  const idx = Math.abs(projectId) % PROJECT_COLOR_PALETTE.length;
  return PROJECT_COLOR_PALETTE[idx]!;
}

/** Read activity `screen_hours` from timesheet-data (snake_case or camelCase). */
export function parseActivityScreenHours(
  act: ProjectTimesheetDataActivity | Record<string, unknown>,
): number {
  const raw = act as Record<string, unknown>;
  const v =
    raw['screen_hours'] ??
    raw['screenHours'] ??
    raw['ScreenHours'] ??
    raw['screen_hours_detected'];
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function roundHoursToTwo(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Split activity `screen_hours` across tasks by logged-hours share:
 * `(taskLogged / totalLogged) * screenHours`.
 * Last task receives the remainder so the parts still sum to the activity total.
 */
export function splitScreenHoursByLoggedShare(
  totalScreenHours: number,
  loggedHoursPerTask: readonly number[],
): number[] {
  const n = loggedHoursPerTask.length;
  if (n === 0) return [];
  if (!Number.isFinite(totalScreenHours) || totalScreenHours <= 0) {
    return loggedHoursPerTask.map(() => 0);
  }
  const totals = loggedHoursPerTask.map((h) => (Number(h) > 0 ? Number(h) : 0));
  const totalLogged = totals.reduce((s, h) => s + h, 0);
  if (!Number.isFinite(totalLogged) || totalLogged <= 0) {
    // No logged hours to weight by — keep prior behavior (full screen on first task).
    return totals.map((_, idx) => (idx === 0 ? roundHoursToTwo(totalScreenHours) : 0));
  }
  let running = 0;
  return totals.map((logged, idx) => {
    const isLast = idx === n - 1;
    const raw = totalScreenHours * (logged / totalLogged);
    const split = isLast ? roundHoursToTwo(totalScreenHours - running) : roundHoursToTwo(raw);
    running += split;
    return split;
  });
}

/**
 * Format screen hours for display. Values under 0.1h (e.g. 0.03) must not round to 0.0 via `1.1-1`.
 */
export function formatScreenHoursForDisplay(hours: number): string {
  const n = Number(hours);
  if (!Number.isFinite(n) || n <= 0) return '0.0';
  if (n < 0.1) return n.toFixed(2);
  return n.toFixed(1);
}

/**
 * Human-readable duration from decimal hours.
 * Drops leftover seconds (0.76 h = 45 m 36 s → "45 min") so they cannot round up to an extra minute.
 */
export function formatDurationFromHours(hours: number): string {
  return formatCompactDurationFromHours(hours);
}

/** Parse human-readable duration or decimal hours back to decimal hours for API payloads. */
export function parseDurationToHours(raw: string, preferMinutes = false): number | null {
  const s = raw.trim();
  if (!s) return null;

  const colon = /^(\d+):(\d{1,2})$/.exec(s);
  if (colon) {
    const h = parseInt(colon[1], 10);
    const m = parseInt(colon[2], 10);
    if (!Number.isFinite(h) || !Number.isFinite(m) || m >= 60) return null;
    return h + m / 60;
  }

  const hoursAndMinutes = /^(\d+)\s*h(?:our)?s?\s+(\d+)\s*m(?:in(?:ute)?s?)?(?:\s+(\d+)\s*s(?:ec(?:ond)?s?)?)?$/i.exec(s);
  if (hoursAndMinutes) {
    const sec = hoursAndMinutes[3] ? parseInt(hoursAndMinutes[3], 10) : 0;
    return parseInt(hoursAndMinutes[1], 10) + parseInt(hoursAndMinutes[2], 10) / 60 + sec / 3600;
  }

  const minutesAndSeconds = /^(\d+)\s*m(?:in(?:ute)?s?)?\s+(\d+)\s*s(?:ec(?:ond)?s?)?$/i.exec(s);
  if (minutesAndSeconds) {
    return parseInt(minutesAndSeconds[1], 10) / 60 + parseInt(minutesAndSeconds[2], 10) / 3600;
  }

  const hoursOnly = /^(\d+(?:\.\d+)?)\s*h(?:our)?s?$/i.exec(s);
  if (hoursOnly) {
    const h = parseFloat(hoursOnly[1]);
    return Number.isFinite(h) ? h : null;
  }

  const minutesOnly = /^(\d+)\s*m(?:in(?:ute)?s?)?$/i.exec(s);
  if (minutesOnly) {
    return parseInt(minutesOnly[1], 10) / 60;
  }

  const secondsOnly = /^(\d+)\s*s(?:ec(?:ond)?s?)?$/i.exec(s);
  if (secondsOnly) {
    return parseInt(secondsOnly[1], 10) / 3600;
  }

  if (/^\d*\.\d+$/.test(s)) {
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }

  if (/^\d+$/.test(s)) {
    const n = parseInt(s, 10);
    if (!Number.isFinite(n)) return null;
    return preferMinutes ? n / 60 : n;
  }

  return null;
}

function inferActivityMapped(act: ProjectTimesheetDataActivity, activityType: TimesheetActivityType | null): boolean {
  if (typeof act.is_mapped === 'boolean') return act.is_mapped;
  const tasks = act.tasks ?? [];
  if (!tasks.length) return false;
  if (activityType === 'manual') return true;
  return tasks.some(
    (t) => !!(t.task_name && String(t.task_name).trim()) || !!(t.stage_name && String(t.stage_name).trim()),
  );
}

/** Maps GET timesheet-data project row `status` into {@link WeekStatus}. */
function normalizeApiProjectDayStatus(raw: string | undefined | null): WeekStatus | undefined {
  const t = (raw ?? '').trim().toLowerCase();
  if (t === 'draft' || t === 'revoked') return t === 'revoked' ? 'revoked' : 'draft';
  if (t === 'submitted' || t === 'pending') return 'submitted';
  if (t === 'approved') return 'approved';
  if (t === 'rejected') return 'rejected';
  return undefined;
}

function mergeDailySubmissionStatus(a: WeekStatus, b: WeekStatus): WeekStatus {
  const locked = (s: WeekStatus) => s === 'submitted' || s === 'approved';
  if (locked(a) && locked(b)) return a === 'approved' && b === 'approved' ? 'approved' : 'submitted';
  if (locked(a)) return a;
  if (locked(b)) return b;
  if (a === 'rejected' || b === 'rejected') return 'rejected';
  return 'draft';
}

/**
 * Backend `billingType` is typically `'Billable' | 'Non Billable'` (spacing/casing may vary).
 * Hyphenated or single-token variants are accepted via a compact key.
 */
function parseBillingTypeToBoolean(value: unknown): boolean | null {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  if (!normalized) return null;
  const compact = normalized.replace(/[\s_-]/g, '');
  if (compact === 'billable') return true;
  if (compact === 'nonbillable' || compact === 'notbillable' || compact === 'unbillable') {
    return false;
  }
  return null;
}

function coerceBooleanish(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value === 0) return false;
    if (value === 1) return true;
  }
  const s = String(value ?? '').trim().toLowerCase();
  if (s === 'true' || s === 'yes' || s === '1') return true;
  if (s === 'false' || s === '0') return false;
  return null;
}

/**
 * Billing fields only — when absent, returns `null` so callers can fall back (e.g. user-project-task-board).
 */
export function parseBillableFromTaskPayload(task: unknown): boolean | null {
  const rec = task as Record<string, unknown>;
  const billingRaw =
    rec['billingType'] ??
    rec['BillingType'] ??
    rec['billing_type'] ??
    rec['billingtype'];
  const byType = parseBillingTypeToBoolean(billingRaw);
  if (byType != null) return byType;
  const categoryRaw =
    rec['taskCategory'] ??
    rec['TaskCategory'] ??
    rec['task_category'] ??
    rec['category'] ??
    rec['Category'];
  const byCategory = parseBillingTypeToBoolean(categoryRaw);
  if (byCategory != null) return byCategory;
  const boolRaw =
    rec['isBillable'] ?? rec['IsBillable'] ?? rec['is_billable'] ?? rec['is_Billable'];
  const coerced = coerceBooleanish(boolRaw);
  if (coerced != null) return coerced;
  return null;
}

/**
 * Resolves billable from task-board or GET timesheet-data `tasks[]` payloads.
 * When the payload has no billing signal, defaults to billable (legacy / non-RA rows).
 */
export function resolveBillableFromTaskPayload(task: unknown): boolean {
  return parseBillableFromTaskPayload(task) ?? true;
}

/**
 * Backend may send phase/milestone as a string or nested object ({ phaseName, … }).
 * Never use String(object) — that renders "[object Object]" in the UI.
 */
function coerceTimesheetHierarchyLabel(
  value: unknown,
  kind: 'phase' | 'milestone',
): string {
  if (value == null) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '[object Object]' ? '' : trimmed;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'object') return '';

  const rec = value as Record<string, unknown>;
  const keys =
    kind === 'phase'
      ? [
          'phaseName',
          'PhaseName',
          'phase_name',
          'stageName',
          'StageName',
          'stage_name',
          'name',
          'Name',
          'label',
          'Label',
          'title',
          'Title',
        ]
      : [
          'milestoneName',
          'MilestoneName',
          'milestone_name',
          'name',
          'Name',
          'label',
          'Label',
          'title',
          'Title',
        ];

  for (const key of keys) {
    const nested = rec[key];
    if (typeof nested === 'string' && nested.trim()) return nested.trim();
  }
  return '';
}

function coerceTimesheetHierarchyDescription(
  value: unknown,
  kind: 'phase' | 'milestone',
): string {
  if (value == null || typeof value !== 'object') return '';

  const rec = value as Record<string, unknown>;
  const keys =
    kind === 'phase'
      ? [
          'phaseDescription',
          'PhaseDescription',
          'phase_description',
          'stageDescription',
          'StageDescription',
          'stage_description',
          'description',
          'Description',
        ]
      : [
          'milestoneDescription',
          'MilestoneDescription',
          'milestone_description',
          'description',
          'Description',
        ];

  for (const key of keys) {
    const nested = rec[key];
    if (typeof nested === 'string') {
      const trimmed = nested.trim();
      if (trimmed && trimmed !== '[object Object]') return trimmed;
    }
  }
  return '';
}

function resolveTaskPhaseDescription(task: ProjectTimesheetDataActivity['tasks'][number]): string {
  const rec = task as unknown as Record<string, unknown>;
  const topLevel = [
    rec['phase_description'],
    rec['phaseDescription'],
    rec['PhaseDescription'],
    rec['stage_description'],
    rec['stageDescription'],
    rec['StageDescription'],
  ];
  for (const candidate of topLevel) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  }
  const objectCandidates = [rec['phase'], rec['Phase'], rec['stage'], rec['Stage']];
  for (const candidate of objectCandidates) {
    const desc = coerceTimesheetHierarchyDescription(candidate, 'phase');
    if (desc) return desc;
  }
  return '';
}

function resolveTaskMilestoneDescription(task: ProjectTimesheetDataActivity['tasks'][number]): string {
  const rec = task as unknown as Record<string, unknown>;
  const topLevel = [
    rec['milestone_description'],
    rec['milestoneDescription'],
    rec['MilestoneDescription'],
  ];
  for (const candidate of topLevel) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  }
  const objectCandidates = [rec['milestone'], rec['Milestone']];
  for (const candidate of objectCandidates) {
    const desc = coerceTimesheetHierarchyDescription(candidate, 'milestone');
    if (desc) return desc;
  }
  return '';
}

function resolveTaskPhaseName(task: ProjectTimesheetDataActivity['tasks'][number]): string {
  const rec = task as unknown as Record<string, unknown>;
  const candidates = [
    rec['phase'],
    rec['Phase'],
    rec['phase_name'],
    rec['phaseName'],
    rec['stage'],
    rec['Stage'],
    rec['stage_name'],
    rec['stageName'],
  ];
  for (const candidate of candidates) {
    const label = coerceTimesheetHierarchyLabel(candidate, 'phase');
    if (label) return label;
  }
  return '';
}

function resolveTaskMilestoneName(task: ProjectTimesheetDataActivity['tasks'][number]): string {
  const rec = task as unknown as Record<string, unknown>;
  const candidates = [
    rec['milestone'],
    rec['Milestone'],
    rec['milestone_name'],
    rec['milestoneName'],
    rec['ms'],
  ];
  for (const candidate of candidates) {
    const label = coerceTimesheetHierarchyLabel(candidate, 'milestone');
    if (label) return label;
  }
  return '';
}

function resolveTaskBillableForMappedEntry(
  task: ProjectTimesheetDataActivity['tasks'][number],
  projectId: number,
  taskBoardBillableByProjectTask: ReadonlyMap<string, boolean> | null,
): boolean {
  const explicit = parseBillableFromTaskPayload(task);
  if (explicit !== null) return explicit;
  const rawId = task.task_id ?? task.taskId;
  const idNum = Number(rawId);
  if (Number.isFinite(idNum) && idNum > 0) {
    const key = `${projectId}|${idNum}`;
    if (taskBoardBillableByProjectTask?.has(key)) {
      return taskBoardBillableByProjectTask.get(key)!;
    }
  }
  return true;
}

function mapApiDayProjectsToDetails(projects: ProjectTimesheetDataProjectDay[]): MonthDayProjectDetail[] {
  return projects.map((p) => {
    let loggedHours = 0;
    let screenHours = 0;
    const tasks: MonthDayProjectTaskDetail[] = [];
    for (const act of p.activity ?? []) {
      screenHours += parseActivityScreenHours(act);
      for (const t of act.tasks ?? []) {
        const hrs = Number(t.logged_hours) || 0;
        loggedHours += hrs;
        const rawId = t.task_id ?? t.taskId;
        const name = (t.task_name ?? '').trim();
        tasks.push({
          taskName: name || (rawId != null ? `Task #${rawId}` : 'Task'),
          loggedHours: hrs,
          comment: t.comment ?? null,
        });
      }
    }
    const status = normalizeApiProjectDayStatus(p.status) ?? 'draft';
    const statusRaw = (p.status ?? '').trim() || status;
    return {
      projectId: p.project_id,
      projectName: p.project_name,
      projectCode: p.project_code,
      projectType: formatTimesheetProjectTypeLabel(p.project_type),
      status,
      statusRaw,
      loggedHours,
      screenHours,
      tasks,
    };
  });
}

function deriveDayProjectsFromEntries(dayEntries: TimesheetEntry[]): MonthDayProjectDetail[] {
  const byProject = new Map<string, TimesheetEntry[]>();
  for (const e of dayEntries) {
    const key =
      e.timesheetProjectId != null && e.timesheetProjectId > 0
        ? `id:${e.timesheetProjectId}`
        : `name:${e.proj}`;
    const list = byProject.get(key) ?? [];
    list.push(e);
    byProject.set(key, list);
  }
  return [...byProject.values()].map((entries) => {
    const first = entries[0];
    const tasks: MonthDayProjectTaskDetail[] = [];
    for (const e of entries) {
      if (!e.task?.trim()) continue;
      tasks.push({
        taskName: e.task.trim(),
        loggedHours: e.hrs,
        comment: e.comment?.trim() ? e.comment.trim() : null,
      });
    }
    const status = first.projectDayStatus ?? 'draft';
    return {
      projectId: first.timesheetProjectId ?? 0,
      projectName: first.proj,
      projectCode: first.projectCode ?? '',
      projectType: formatTimesheetProjectTypeLabel(first.type === 'sup' ? 'SUPPORT' : 'RA'),
      status,
      statusRaw: first.projectDayStatusRaw ?? status,
      loggedHours: entries.reduce((s, e) => s + e.hrs, 0),
      screenHours: entries.reduce((s, e) => s + e.screen, 0),
      tasks,
    };
  });
}

/** True when API `project_type` is Support — skip user-project-task-board (RA board only). */
export function isSupportTimesheetProjectType(projectType: string | undefined | null): boolean {
  return String(projectType ?? '').trim().toUpperCase() === 'SUPPORT';
}

/** UI label for API `project_type` (e.g. RA → CRM, SUPPORT → Support). */
export function formatTimesheetProjectTypeLabel(projectType: string | undefined | null): string {
  return isSupportTimesheetProjectType(projectType) ? 'Support' : 'CRM';
}

function collectProjectIdsFromTimesheetByDate(
  byDate: Record<string, ProjectTimesheetDataProjectDay[] | undefined>,
): number[] {
  const s = new Set<number>();
  for (const arr of Object.values(byDate)) {
    if (!Array.isArray(arr)) continue;
    for (const p of arr) {
      if (isSupportTimesheetProjectType(p.project_type)) continue;
      const id = Number(p.project_id);
      if (Number.isFinite(id) && id > 0) s.add(id);
    }
  }
  return [...s];
}

/** Builds `projectId|taskId` → billable from GET user-project-task-board `phases`. */
function extractBillableLookupForProject(projectId: number, phasesRaw: unknown): Map<string, boolean> {
  const out = new Map<string, boolean>();
  if (!Array.isArray(phasesRaw)) return out;
  for (const ph of phasesRaw) {
    const mr = ph as Record<string, unknown>;
    const milestonesRaw = mr['milestones'] ?? mr['Milestones'];
    if (!Array.isArray(milestonesRaw)) continue;
    for (const ms of milestonesRaw) {
      const mrec = ms as Record<string, unknown>;
      const tasksRaw = mrec['tasks'] ?? mrec['Tasks'];
      if (!Array.isArray(tasksRaw)) continue;
      for (const t of tasksRaw) {
        const rec = t as Record<string, unknown>;
        const tid = Number(rec['taskId'] ?? rec['TaskId'] ?? rec['task_id']);
        if (!Number.isFinite(tid) || tid <= 0) continue;
        out.set(`${projectId}|${tid}`, resolveBillableFromTaskPayload(t));
      }
    }
  }
  return out;
}

/** Maps GET /project-timesheet/timesheet-data into one `TimesheetEntry` per CRM task line (weekly/daily task rows). */
function mapTimesheetDataByDateToEntries(
  byDate: Record<string, ProjectTimesheetDataProjectDay[] | undefined>,
  taskBoardBillableByProjectTask: ReadonlyMap<string, boolean> | null = null,
): TimesheetEntry[] {
  const out: TimesheetEntry[] = [];
  for (const [dateStr, projects] of Object.entries(byDate)) {
    if (!Array.isArray(projects)) continue;
    for (const p of projects) {
      const projectDayStatus = normalizeApiProjectDayStatus(p.status);
      const statusRawTrim = (p.status ?? '').trim();
      const activities = p.activity ?? [];
      activities.forEach((act, activityIndex) => {
        const tasks = act.tasks ?? [];
        const scr = parseActivityScreenHours(act);
        const activityType = normalizeActivityType(act.activity_type);
        const title = activityTitle(activityType);
        const activityTitleLabel = String(act.activity_title ?? act.activityTitle ?? '').trim();
        const isAuto = activityType !== 'manual';
        const activityMapped = inferActivityMapped(act, activityType);
        const rawActId = act.activity_id ?? act.activityId;
        const actIdNum = Number(rawActId);
        const timesheetActivityId =
          Number.isFinite(actIdNum) && actIdNum > 0 ? actIdNum : undefined;
        const appLabel = activityAppBadgeLabel(act);
        const shared = {
          date: dateStr,
          proj: p.project_name,
          projectCode: p.project_code,
          color: colorForProjectId(p.project_id),
          type: isSupportTimesheetProjectType(p.project_type) ? ('sup' as const) : ('crm' as const),
          billable: true,
          auto: isAuto,
          unrId: null,
          activityType: activityType ?? undefined,
          activityMapped,
          timesheetProjectId: p.project_id,
          activityIndex,
          ...(projectDayStatus != null ? { projectDayStatus } : {}),
          ...(statusRawTrim ? { projectDayStatusRaw: statusRawTrim } : {}),
          ...(appLabel ? { activityAppLabel: appLabel } : {}),
          ...(activityTitleLabel ? { activityTitle: activityTitleLabel } : {}),
          ...(timesheetActivityId != null ? { timesheetActivityId } : {}),
        };

        if (tasks.length === 0) {
          // Logged hours come only from `tasks[]`; empty tasks ⇒ 0 logged even if `screen_hours` is set.
          out.push({
            id: `${API_ENTRY_ID_PREFIX}${dateStr}-${p.project_id}-a${activityIndex}`,
            ...shared,
            stage: '',
            ms: '',
            task: '',
            hrs: 0,
            screen: scr,
            comment: '',
            activityTaskCount: 0,
            activityTaskLines: undefined,
          });
          return;
        }

        const loggedPerTask = tasks.map((t) => Number(t.logged_hours) || 0);
        const screenPerTask = splitScreenHoursByLoggedShare(scr, loggedPerTask);

        tasks.forEach((t, taskIdx) => {
          const rawId = t.task_id ?? t.taskId;
          const idNum = Number(rawId);
          const taskId = Number.isFinite(idNum) && idNum > 0 ? idNum : undefined;
          const taskRec = t as unknown as Record<string, unknown>;
          const commentRaw = taskRec['comment'] ?? taskRec['Comment'];
          const entryComment = commentRaw != null ? String(commentRaw).trim() : '';
          const phaseName = resolveTaskPhaseName(t);
          const milestoneName = resolveTaskMilestoneName(t);
          const phaseDescription = resolveTaskPhaseDescription(t);
          const milestoneDescription = resolveTaskMilestoneDescription(t);
          const lineHrs = loggedPerTask[taskIdx] ?? 0;
          const line: TimesheetActivityTaskLine = {
            ...(taskId != null ? { taskId } : {}),
            loggedHours: lineHrs,
            stageName: phaseName || undefined,
            ...(phaseDescription ? { stageDescription: phaseDescription } : {}),
            milestoneName: milestoneName || undefined,
            ...(milestoneDescription ? { milestoneDescription: milestoneDescription } : {}),
            taskName: t.task_name,
            comment: entryComment,
          };
          const name = String(t.task_name ?? '').trim();
          const displayTask = name || (taskId != null ? `Task #${taskId}` : title);
          const stage = phaseName;
          const ms = milestoneName;
          out.push({
            id: `${API_ENTRY_ID_PREFIX}${dateStr}-${p.project_id}-a${activityIndex}-t${taskIdx}`,
            ...shared,
            billable: resolveTaskBillableForMappedEntry(t, p.project_id, taskBoardBillableByProjectTask),
            stage,
            ms,
            task: displayTask,
            hrs: lineHrs,
            screen: screenPerTask[taskIdx] ?? 0,
            comment: entryComment,
            activityTaskCount: 1,
            activityTaskLines: [line],
          });
        });
      });
    }
  }
  return out;
}

function mapApiUnreportedToActivity(row: ProjectTimesheetUnreportedEntry, mapped: boolean): UnreportedActivity {
  const appName = timesheetAppDisplayName(row.appId);
  const remark = (row.remark || '').trim();
  const taskNm = (row.taskName || '').trim();
  const placeholderRemark = remark === '' || remark === 'NA' || remark === 'N/A';
  const title = !placeholderRemark ? remark : taskNm !== '' ? taskNm : appName;
  return {
    id: String(row.id),
    title,
    appId: row.appId,
    appName,
    hrs: Number(row.screenHours) || 0,
    date: row.date,
    mapped,
  };
}

@Injectable({ providedIn: 'root' })
export class TimesheetService {
  private readonly timesheetManagementApi = inject(TimesheetManagementApiService);
  private readonly projectManagementService = inject(ProjectManagementService);
  private readonly activityApps = inject(ActivityAppsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngxUiLoader = inject(NgxUiLoaderService);
  private readonly cookieService = inject(CookieService);
  /** Real calendar “today” (local) — drives today column/cell highlights. */
  readonly today = signal<Date>(TimesheetService.startOfLocalDay(new Date()));
  readonly currentView = signal<ViewMode>('daily');
  /**
   * Review & Approval shell: topbar always uses week calendar and ±7 day navigation
   * even if Time Entries was left on Daily/Monthly.
   */
  readonly approvalShellMode = signal(false);
  /** View anchor date (may differ from `today` while browsing weeks/months). */
  readonly currentDate = signal<Date>(TimesheetService.startOfLocalDay(new Date()));
  readonly selectedMonthDay = signal<string | null>(null);
  /** Weekly unreported panel day chip; synced when the topbar week changes. */
  readonly selectedWeekDay = signal<string | null>(null);
  /** Email deep-link: project row to highlight in weekly view (cleared on week/view change). */
  private readonly _highlightedProjectId = signal<number | null>(null);
  readonly highlightedProjectId = this._highlightedProjectId.asReadonly();
  private deepLinkHighlightWeekKey: string | null = null;
  /**
   * After mapping an unreported entry, scroll to this project card (daily/weekly).
   * Cleared by the matching project group / weekly row once scrolled.
   */
  private readonly _scrollToMappedProjectId = signal<number | null>(null);
  readonly scrollToMappedProjectId = this._scrollToMappedProjectId.asReadonly();
  readonly entries = signal<TimesheetEntry[]>([]);
  /** Raw GET timesheet-data `by_date` for month-view hover details. */
  readonly timesheetByDate = signal<Record<string, ProjectTimesheetDataProjectDay[]>>({});
  readonly unreported = signal<UnreportedActivity[]>([]);
  readonly weekStatus = signal<Record<string, WeekStatus>>({});
  readonly approvedHistory = signal<Record<string, ApprovalHistoryEvent[]>>({});
  readonly sidePanelSearch = signal<string>('');
  /** @deprecated Use SnackbarService. Kept for TsToastComponent backward compat. */
  readonly toastMessage = signal<string | null>(null);

  /** Set from auth / employee API; drives CRM vs Support entry UI. */
  readonly employeeProfile = signal<TimesheetEmployeeProfile>({
    category: 'technical',
    assignedCrmProjects: [],
  });

  /** Assigned tasks for the week; set from API. */
  readonly assignedTasks = signal<AssignedTask[]>([]);

  /** Projects from GET /project-timesheet/project-list (Add Entry). */
  readonly projectTimesheetProjectList = signal<ProjectTimesheetListItem[] | null>(null);
  readonly projectTimesheetProjectListLoading = signal(false);

  constructor() {
    effect(() => {
      if (!this.activityApps.loaded()) return;
      void this.activityApps.apps();
      this.unreported.update((list) =>
        list.map((u) =>
          u.appId != null ? { ...u, appName: this.activityApps.nameFor(u.appId, u.appName) } : u,
        ),
      );
    });
    combineLatest([toObservable(this.currentView), toObservable(this.currentDate)])
      .pipe(
        debounceTime(0),
        map(([view, ref]) => ({
          view,
          range: this.unreportedQueryRange(ref, view),
        })),
        distinctUntilChanged(
          (a, b) =>
            a.view === b.view && a.range.start === b.range.start && a.range.end === b.range.end,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ range }) => {
        this.fetchProjectTimesheetForRange(range.start, range.end);
      });
    effect(() => {
      if (this.currentView() !== 'weekly') {
        this.selectedWeekDay.set(null);
        return;
      }
      const week = this.getWeekDays(this.currentDate());
      if (!week.length) {
        this.selectedWeekDay.set(null);
        return;
      }
      const weekStrs = week.map((d) => this.dateToString(d));
      const selected = this.selectedWeekDay();
      if (selected && weekStrs.includes(selected)) return;
      const anchorDay = this.dateToString(this.currentDate());
      this.selectedWeekDay.set(weekStrs.includes(anchorDay) ? anchorDay : this.dateToString(week[0]!));
    });
    effect(() => {
      if (this._highlightedProjectId() == null) return;
      const view = this.currentView();
      const weekKey = this.currentWeekKey();
      if (view !== 'weekly' || weekKey !== this.deepLinkHighlightWeekKey) {
        this._highlightedProjectId.set(null);
        this.deepLinkHighlightWeekKey = null;
      }
    });
  }

  /** Date range for GET /project-timesheet/unreported-entries: day / week Mon–Sun / calendar month. */
  unreportedQueryRange(ref: Date, view: ViewMode): { start: string; end: string } {
    if (view === 'daily') {
      const d = this.dateToString(ref);
      return { start: d, end: d };
    }
    if (view === 'weekly') {
      const days = this.getWeekDays(ref);
      return { start: this.dateToString(days[0]), end: this.dateToString(days[6]) };
    }
    const y = ref.getFullYear();
    const m = ref.getMonth();
    const start = new Date(y, m, 1);
    const end = new Date(y, m + 1, 0);
    return { start: this.dateToString(start), end: this.dateToString(end) };
  }

  /** Re-fetch unreported rows and timesheet-data for the active view range (e.g. after map save). */
  refreshProjectTimesheetFromApi(): void {
    const range = this.unreportedQueryRange(this.currentDate(), this.currentView());
    this.fetchProjectTimesheetForRange(range.start, range.end);
  }

  /**
   * Loads unreported-entries + timesheet-data in parallel (still excluded from global HTTP loader).
   * Uses the app default ngx-ui-loader (square-jelly-box) until both complete.
   */
  private fetchProjectTimesheetForRange(startDate: string, endDate: string): void {
    this.ngxUiLoader.start();
    const skipUnreported = this.approvalShellMode();
    forkJoin({
      unreported: skipUnreported
        ? of(null as ProjectTimesheetUnreportedEntriesResponse | null)
        : this.timesheetManagementApi.getProjectTimesheetUnreportedEntries(startDate, endDate).pipe(
            take(1),
            catchError(() => of(null as ProjectTimesheetUnreportedEntriesResponse | null)),
          ),
      timesheet: this.timesheetManagementApi.getProjectTimesheetTimesheetData(startDate, endDate).pipe(
        take(1),
        catchError(() => of(null as ProjectTimesheetTimesheetDataResponse | null)),
      ),
    })
      .pipe(
        switchMap(({ unreported, timesheet }) => {
          if (!skipUnreported) {
            this.applyUnreportedEntriesResponse(unreported);
          }
          if (!timesheet?.success || !timesheet.data?.by_date) {
            this.applyTimesheetDataResponse(timesheet, null);
            return of(undefined);
          }
          const userId = timesheet.data.user_id;
          const projectIds = collectProjectIdsFromTimesheetByDate(timesheet.data.by_date);
          if (!userId || projectIds.length === 0) {
            this.applyTimesheetDataResponse(timesheet, null);
            return of(undefined);
          }
          return forkJoin(
            projectIds.map((projectId) =>
              this.projectManagementService.getUserProjectTaskBoard(projectId, userId).pipe(
                take(1),
                map((r) => ({ projectId, r })),
                catchError(() => of({ projectId, r: null as null })),
              ),
            ),
          ).pipe(
            tap((pairs) => {
              const merged = new Map<string, boolean>();
              for (const { projectId, r } of pairs) {
                if (!r?.success || !r.data) continue;
                const phases = r.data.phases;
                extractBillableLookupForProject(projectId, phases).forEach((bill, key) =>
                  merged.set(key, bill),
                );
              }
              this.applyTimesheetDataResponse(timesheet, merged.size > 0 ? merged : null);
            }),
          );
        }),
        finalize(() => this.ngxUiLoader.stop()),
      )
      .subscribe();
  }

  private applyUnreportedEntriesResponse(res: ProjectTimesheetUnreportedEntriesResponse | null): void {
    if (!res?.success || !Array.isArray(res.data)) {
      this.unreported.set([]);
      return;
    }
    if (res.data.length === 0) {
      this.unreported.set([]);
      return;
    }
    const prevMapped = new Map(this.unreported().map((u) => [u.id, u.mapped]));
    const next = res.data.map((row) =>
      mapApiUnreportedToActivity(row, prevMapped.get(String(row.id)) ?? false),
    );
    this.unreported.set(next);
  }

  private applyTimesheetDataResponse(
    res: ProjectTimesheetTimesheetDataResponse | null,
    taskBoardBillableByProjectTask: ReadonlyMap<string, boolean> | null = null,
  ): void {
    if (!res?.success || !res.data?.by_date) {
      this.timesheetByDate.set({});
      this.entries.update((arr) => arr.filter((e) => !e.id.startsWith(API_ENTRY_ID_PREFIX)));
      return;
    }
    this.timesheetByDate.set(res.data.by_date);
    const apiRows = mapTimesheetDataByDateToEntries(res.data.by_date, taskBoardBillableByProjectTask);
    this.entries.update((arr) => [
      ...arr.filter((e) => !e.id.startsWith(API_ENTRY_ID_PREFIX)),
      ...apiRows,
    ]);
  }

  /** Call when backend returns the signed-in employee’s timesheet profile. */
  setEmployeeProfile(profile: TimesheetEmployeeProfile): void {
    this.employeeProfile.set(profile);
  }

  setProjectTimesheetProjectList(data: ProjectTimesheetListItem[] | null): void {
    this.projectTimesheetProjectList.set(data);
  }

  /** Last `date` query sent to GET /project-timesheet/project-list. */
  private projectTimesheetProjectListDate: string | null = null;

  /** GET /project-timesheet/project-list?date= — shared by Log Time Entry and map/add-task modals. */
  loadProjectTimesheetProjectList(date?: string): void {
    const resolved = (date ?? '').trim() || this.dateToString(this.currentDate());
    if (
      this.projectTimesheetProjectListLoading() &&
      this.projectTimesheetProjectListDate === resolved
    ) {
      return;
    }
    this.projectTimesheetProjectListDate = resolved;
    this.projectTimesheetProjectListLoading.set(true);
    this.timesheetManagementApi
      .getProjectTimesheetProjectList(resolved)
      .pipe(finalize(() => this.projectTimesheetProjectListLoading.set(false)))
      .subscribe({
        next: (res) => {
          if (this.projectTimesheetProjectListDate !== resolved) return;
          const normalized = normalizeProjectTimesheetListItems(res?.data);
          if (res?.success && normalized.length > 0) {
            this.setProjectTimesheetProjectList(normalized);
            this.syncAssignedCrmProjectsFromList(normalized);
            return;
          }
          if (res?.success && Array.isArray(res.data) && res.data.length === 0) {
            this.setProjectTimesheetProjectList([]);
            return;
          }
          this.setProjectTimesheetProjectList(null);
        },
        error: () => {
          if (this.projectTimesheetProjectListDate !== resolved) return;
          this.setProjectTimesheetProjectList(null);
        },
      });
  }

  /** When employee profile is support + has CRM card, derive allowed CRM names from project-list. */
  private syncAssignedCrmProjectsFromList(list: ProjectTimesheetListItem[]): void {
    const profile = this.employeeProfile();
    if (profile.category === 'technical' || profile.assignedCrmProjects.length > 0) return;
    const crmNames = list
      .filter((p) => !isSupportTimesheetProjectType(p.project_type))
      .map((p) => String(p.name ?? '').trim())
      .filter(Boolean);
    if (!crmNames.length) return;
    this.employeeProfile.set({ ...profile, assignedCrmProjects: crmNames });
  }

  /** Fallback CRM project rows from loaded timesheet-data when project-list is empty. */
  deriveCrmProjectsFromTimesheetData(): ProjectTimesheetListItem[] {
    const byId = new Map<number, ProjectTimesheetListItem>();
    for (const arr of Object.values(this.timesheetByDate())) {
      if (!Array.isArray(arr)) continue;
      for (const row of arr) {
        if (isSupportTimesheetProjectType(row.project_type)) continue;
        const id = Number(row.project_id);
        const name = String(row.project_name ?? '').trim();
        if (!Number.isFinite(id) || id <= 0 || !name || byId.has(id)) continue;
        byId.set(id, {
          id,
          name,
          projectCode: String(row.project_code ?? '').trim(),
          status: String(row.status ?? '').trim(),
          allStages: [],
          project_type: row.project_type,
        });
      }
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }

  /** UI color for a CRM project name from project list; otherwise `fallback` (e.g. group color). */
  projectColorForName(projectName: string, fallback: string): string {
    const item = this.projectTimesheetProjectList()?.find((x) => x.name === projectName);
    if (item) return colorForProjectId(item.id);
    return fallback;
  }

  /** Display formatter for detected screen hours (preserves sub‑0.1h values). */
  formatScreenHours(hours: number): string {
    return formatScreenHoursForDisplay(hours);
  }

  /** User-friendly duration label for unallocated / activity time (e.g. "15 min", "1 hr 15 min"). */
  formatDuration(hours: number): string {
    return formatDurationFromHours(hours);
  }

  /** Dense duration for tight UI columns (e.g. "10 h 21 m"). */
  formatDurationHm(hours: number): string {
    return formatDenseDurationFromHours(hours);
  }

  readonly currentWeekDays = computed<WeekDay[]>(() =>
    this.getWeekDays(this.currentDate()).map((date) => {
      const dateStr = this.dateToString(date);
      const dayEntries = this.entries().filter((e) => e.date === dateStr);
      const totalHrs = sumHoursTruncatedToMinutes(dayEntries.map((e) => e.hrs));
      return {
        date,
        dateStr,
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        isToday: dateStr === this.dateToString(this.today()),
        isFuture: dateStr > this.dateToString(this.today()),
        totalHrs,
      };
    }),
  );
  readonly currentWeekKey = computed<string>(() => this.getWeekKey(this.currentDate()));
  /**
   * Effective status for the week containing `currentDate` (same rollup as the weekly view chip:
   * project–day cells + `weekStatus` map fallback).
   */
  readonly currentWeekStatus = computed<WeekStatus>(() => {
    const ref = this.currentDate();
    const days = this.currentWeekDays().map((d) => d.dateStr);
    const fallback = this.getWeekStatus(ref);
    return aggregateWeekStatusFromEntries(this.currentWeekEntries(), days, fallback);
  });
  /** True when the topbar week has reached the submit window (Sat for current week; after Sun for past weeks). */
  readonly hasSelectedWeekReachedSubmitEligibility = computed<boolean>(() => {
    const days = this.getWeekDays(this.currentDate());
    return hasWeekReachedSubmitEligibility(days, this.today(), (d) => this.dateToString(d));
  });

  /** True when today falls in the Mon–Sun week currently shown in the topbar. */
  readonly isViewingCalendarCurrentWeek = computed<boolean>(() => {
    const days = this.getWeekDays(this.currentDate());
    if (days.length < 7) return false;
    const todayStr = this.dateToString(this.today());
    return isDateWithinWeekRange(
      todayStr,
      this.dateToString(days[0]!),
      this.dateToString(days[6]!),
    );
  });

  /** At least one logged time entry (hours > 0) in the week shown in the topbar. */
  readonly selectedWeekHasLoggedEntries = computed<boolean>(() =>
    this.currentWeekEntries().some((e) => e.hrs > 0),
  );

  /** Weekly project groups for the topbar week (with per-project submission status). */
  readonly selectedWeekProjectGroups = computed<WeeklyProjectGroup[]>(() =>
    this.buildWeeklyProjectGroups(this.currentWeekEntries()),
  );

  /** CRM projects in the selected week that have logged hours (> 0). */
  readonly selectedWeekProjectsWithLoggedHours = computed<WeeklyProjectGroup[]>(() =>
    this.selectedWeekProjectGroups().filter((g) => projectGroupLoggedHours(g) > 0),
  );

  /** At least one project with logged hours is still draft or rejected. */
  readonly hasDraftOrRejectedProjectInSelectedWeek = computed<boolean>(() =>
    this.selectedWeekProjectsWithLoggedHours().some((g) => {
      const st = g.weekSubmissionStatus ?? 'draft';
      return st === 'draft' || st === 'rejected';
    }),
  );

  /** Every project with logged hours is approved or pending review (submitted). */
  readonly allProjectsApprovedOrSubmittedInSelectedWeek = computed<boolean>(() => {
    const projects = this.selectedWeekProjectsWithLoggedHours();
    if (projects.length === 0) return false;
    return projects.every((g) => {
      const st = g.weekSubmissionStatus ?? 'draft';
      return st === 'approved' || st === 'submitted';
    });
  });

  readonly allProjectsApprovedInSelectedWeek = computed<boolean>(() => {
    const projects = this.selectedWeekProjectsWithLoggedHours();
    if (projects.length === 0) return false;
    return projects.every((g) => (g.weekSubmissionStatus ?? 'draft') === 'approved');
  });

  readonly anyProjectSubmittedInSelectedWeek = computed<boolean>(() =>
    this.selectedWeekProjectsWithLoggedHours().some(
      (g) => (g.weekSubmissionStatus ?? 'draft') === 'submitted',
    ),
  );

  /** Projects in the selected week with at least one pending (submitted) project-day. */
  readonly pendingProjectsInSelectedWeek = computed<WeeklyProjectGroup[]>(() =>
    this.selectedWeekProjectsWithLoggedHours().filter(
      (g) => (g.weekSubmissionStatus ?? 'draft') === 'submitted',
    ),
  );

  /** True when any project in the selected week can be recalled (status submitted/pending). */
  readonly canRecallSelectedWeek = computed<boolean>(() => this.pendingProjectsInSelectedWeek().length > 0);

  readonly recallGuardMessage = computed<string>(() => {
    if (!this.selectedWeekHasLoggedEntries()) {
      return 'No timesheet entries for this week.';
    }
    if (!this.canRecallSelectedWeek()) {
      return 'Nothing pending to recall. Only projects awaiting approval can be recalled.';
    }
    return '';
  });

  /**
   * Send / resubmit for the topbar week when at least one project is draft or rejected.
   * Current calendar week: Saturday–Sunday only (Mon–Fri always blocked).
   * Past weeks: allowed after that week ends (covers resubmit after rejection on a prior week).
   */
  readonly canSubmitCurrentWeekForApproval = computed<boolean>(() => {
    if (!this.selectedWeekHasLoggedEntries()) return false;
    if (!this.hasDraftOrRejectedProjectInSelectedWeek()) return false;
    const days = this.getWeekDays(this.currentDate());
    return hasWeekReachedSubmitEligibility(days, this.today(), (d) => this.dateToString(d));
  });
  readonly currentWeekSubmitGuardMessage = computed<string>(() => {
    if (!this.selectedWeekHasLoggedEntries()) {
      return 'Add at least one time entry for this week before sending for approval.';
    }
    if (this.allProjectsApprovedInSelectedWeek()) {
      return 'All projects for this week are already approved. You cannot send for approval again.';
    }
    if (this.allProjectsApprovedOrSubmittedInSelectedWeek()) {
      return 'All projects for this week are pending approval. Send for approval is not available until a project is rejected.';
    }
    const days = this.getWeekDays(this.currentDate());
    if (days.length < 7) return 'Invalid week range.';
    const todayStr = this.dateToString(this.today());
    const weekStartStr = this.dateToString(days[0]!);
    const weekEndStr = this.dateToString(days[6]!);
    if (!hasWeekReachedSubmitEligibility(days, this.today(), (d) => this.dateToString(d))) {
      if (isDateWithinWeekRange(todayStr, weekStartStr, weekEndStr)) {
        return 'Send for approval is available on Saturday and Sunday for the current week. Navigate to a previous week to submit an earlier timesheet.';
      }
      if (todayStr <= weekEndStr) {
        return 'This week has not ended yet. You can send for approval on Saturday or Sunday of this week, or navigate to a previous week once that week is complete.';
      }
      return 'You cannot send a future week for approval.';
    }
    return '';
  });

  /**
   * Any CRM project in the current week has rejected status (entry project-day, weekly
   * project rollup, or week-status fallback — including after all rejected rows are deleted
   * when the backend still reports the week as rejected).
   */
  readonly hasRejectedProjectInCurrentWeek = computed<boolean>(() => {
    if (this.currentWeekStatus() === 'rejected') return true;
    if (this.getWeekStatus(this.currentDate()) === 'rejected') return true;
    const entries = this.currentWeekEntries();
    if (entries.some((e) => e.projectDayStatus === 'rejected')) return true;
    return this.buildWeeklyProjectGroups(entries).some((g) => g.weekSubmissionStatus === 'rejected');
  });

  /**
   * True when any logged project/entry in the selected week has already been through
   * send for approval (submitted/pending, approved, or rejected).
   */
  readonly weekHasEnteredApprovalFlow = computed<boolean>(() => {
    const entries = this.currentWeekEntries();
    if (
      entries.some((e) => {
        const st = e.projectDayStatus ?? 'draft';
        return st === 'submitted' || st === 'approved' || st === 'rejected';
      })
    ) {
      return true;
    }
    return this.selectedWeekProjectsWithLoggedHours().some((g) => {
      const st = g.weekSubmissionStatus ?? 'draft';
      return st === 'submitted' || st === 'approved' || st === 'rejected';
    });
  });

  /**
   * Week-level status-bar Send for approval — first submit / post-revoke only.
   * Hidden once any project is submitted (pending), approved, or rejected
   * (rejected projects use the per-project Send CTA on the weekly row).
   */
  readonly showWeekLevelSendForApproval = computed<boolean>(
    () => !this.weekHasEnteredApprovalFlow(),
  );

  /**
   * True when the selected week rollup is pending review or approved.
   * Blocks new entries on every day in the week (including empty days).
   */
  readonly isSelectedWeekLockedForNewEntries = computed<boolean>(() => {
    if (this.allProjectsApprovedOrSubmittedInSelectedWeek()) return true;
    const st = this.currentWeekStatus();
    return st === 'submitted' || st === 'approved';
  });

  /** At least one project in the selected week is still editable (draft or rejected). */
  readonly hasEditableProjectInSelectedWeek = computed<boolean>(() => {
    if (this.isSelectedWeekLockedForNewEntries()) return false;
    const projects = this.selectedWeekProjectGroups();
    if (projects.length === 0) {
      const s = this.currentWeekStatus();
      return s === 'draft' || s === 'rejected';
    }
    return projects.some((g) => this.isProjectEditableForWeek(g));
  });

  /**
   * Rejected projects use the same submit window as send (Sat–Sun for calendar current week;
   * prior weeks once that week has ended). Mid-week rejection on the current week cannot be
   * resubmitted until Saturday.
   */
  readonly canSubmitOrResubmitCurrentWeek = this.canSubmitCurrentWeekForApproval;

  /** Project week rollup allows add/edit (draft or rejected) when the week is not locked. */
  isProjectEditableForWeek(pg: WeeklyProjectGroup): boolean {
    if (this.isSelectedWeekLockedForNewEntries()) return false;
    const st = pg.weekSubmissionStatus ?? 'draft';
    return st === 'draft' || st === 'rejected';
  }

  /** Project week rollup locks add/edit (submitted or approved), or week is locked. */
  isProjectLockedForWeek(pg: WeeklyProjectGroup): boolean {
    if (this.isSelectedWeekLockedForNewEntries()) return true;
    const st = pg.weekSubmissionStatus ?? 'draft';
    return st === 'submitted' || st === 'approved';
  }

  /**
   * Per-project Send for approval — only for rejected projects with logged hours,
   * using the same Sat–Sun / past-week eligibility window as week-level submit.
   */
  canSubmitProjectForApproval(pg: WeeklyProjectGroup): boolean {
    if ((pg.weekSubmissionStatus ?? 'draft') !== 'rejected') return false;
    if (projectGroupLoggedHours(pg) <= 0) return false;
    const days = this.getWeekDays(this.currentDate());
    return hasWeekReachedSubmitEligibility(days, this.today(), (d) => this.dateToString(d));
  }

  /** API `projectType` for scoped submit/revoke from a weekly project group. */
  resolveWeeklyProjectType(pg: WeeklyProjectGroup): 'RA' | 'SUPPORT' {
    return pg.entryType === 'sup' ? 'SUPPORT' : 'RA';
  }

  readonly currentDayEntries = computed<TimesheetEntry[]>(() => {
    const target = this.currentView() === 'monthly' ? this.selectedMonthDay() : this.dateToString(this.currentDate());
    return this.entries().filter((e) => e.date === target);
  });
  readonly currentWeekEntries = computed<TimesheetEntry[]>(() => {
    const dates = new Set(this.currentWeekDays().map((d) => d.dateStr));
    return this.entries().filter((e) => dates.has(e.date));
  });
  readonly currentMonthEntries = computed<TimesheetEntry[]>(() => {
    const ref = this.currentDate();
    const y = ref.getFullYear();
    const m = ref.getMonth();
    return this.entries().filter((e) => {
      const d = new Date(`${e.date}T12:00:00`);
      return d.getFullYear() === y && d.getMonth() === m;
    });
  });
  /** Daily header: "Wednesday, April 8" style (no year). */
  readonly dateLabelDailyLong = computed<string>(() =>
    this.formatDate(this.dateToString(this.currentDate()), { weekday: 'long', month: 'long', day: 'numeric' }),
  );

  readonly dateLabel = computed<string>(() => {
    const ref = this.currentDate();
    const view = this.navView();
    if (view === 'daily') {
      return this.formatDate(this.dateToString(ref), { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    }
    if (view === 'weekly') {
      const w = this.getWeekDays(ref);
      return `${w[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${w[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return ref.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  });
  readonly filteredUnreportedItems = computed<UnreportedActivity[]>(() => {
    const q = this.sidePanelSearch().trim().toLowerCase();
    if (!q) return this.unreported();
    return this.unreported().filter(
      (u) =>
        u.title.toLowerCase().includes(q) ||
        u.appName.toLowerCase().includes(q) ||
        u.date.includes(q) ||
        (u.timeRange?.toLowerCase().includes(q) ?? false),
    );
  });

  /** Moves date backward by current view granularity. */
  navigatePrev(): void {
    const d = new Date(this.currentDate());
    const view = this.navView();
    if (view === 'daily') d.setDate(d.getDate() - 1);
    else if (view === 'weekly') d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    this.currentDate.set(d);
    this.selectedMonthDay.set(null);
  }

  /** Moves date forward by current view granularity. */
  navigateNext(): void {
    const d = new Date(this.currentDate());
    const view = this.navView();
    if (view === 'daily') d.setDate(d.getDate() + 1);
    else if (view === 'weekly') d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    this.currentDate.set(d);
    this.selectedMonthDay.set(null);
  }

  /** Resets reference date to the real current day. */
  goToToday(): void {
    const t = TimesheetService.startOfLocalDay(new Date());
    this.today.set(t);
    this.currentDate.set(new Date(t));
    this.selectedMonthDay.set(null);
  }

  /**
   * Topbar / calendar: jump to a chosen day, week, or month.
   * - Daily/weekly: anchor to that local calendar day (week is derived Mon–Sun from that day).
   * - Monthly: anchor to the first of the chosen month.
   */
  setAnchorFromCalendar(d: Date): void {
    const t = TimesheetService.startOfLocalDay(d);
    if (Number.isNaN(t.getTime())) return;
    if (this.navView() === 'monthly') {
      this.currentDate.set(new Date(t.getFullYear(), t.getMonth(), 1));
    } else {
      this.currentDate.set(t);
    }
    this.selectedMonthDay.set(null);
  }

  /** Review & Approval route: lock topbar to weekly calendar navigation. */
  setApprovalShellMode(on: boolean): void {
    this.approvalShellMode.set(on);
  }

  /** View mode used for topbar prev/next and date label (weekly on approval shell). */
  private navView(): ViewMode {
    return this.approvalShellMode() ? 'weekly' : this.currentView();
  }

  /** Start of local calendar day (no time drift in comparisons). */
  static startOfLocalDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  /** Changes active timesheet view mode. */
  switchView(view: ViewMode): void {
    this.currentView.set(view);
    this.sidePanelSearch.set('');
    this.selectedMonthDay.set(null);
    // if (view === 'daily') this.currentDate.set(new Date(this.today()));
  }

  /** Bootstrap employee My Timesheet from email deep-link query params. */
  applyDeepLink(params: MyTimesheetDeepLinkParams): void {
    this.switchView('weekly');
    this.currentDate.set(
      TimesheetService.startOfLocalDay(new Date(`${params.weekStart}T12:00:00`)),
    );
    this.deepLinkHighlightWeekKey = params.weekStart;
    this._highlightedProjectId.set(params.projectId ?? null);
  }

  /** Ask the UI to scroll to the project card after a successful map / add. */
  requestScrollToMappedProject(projectId: number | null | undefined): void {
    const id = Number(projectId);
    if (!Number.isFinite(id) || id <= 0) {
      this._scrollToMappedProjectId.set(null);
      return;
    }
    this._scrollToMappedProjectId.set(Math.trunc(id));
  }

  clearScrollToMappedProject(): void {
    this._scrollToMappedProjectId.set(null);
  }

  /** Weekly (and similar): jump to daily view for a specific calendar day `YYYY-MM-DD`. */
  goToDailyForDate(dateStr: string): void {
    const d = new Date(`${dateStr}T12:00:00`);
    if (Number.isNaN(d.getTime())) return;
    this.currentView.set('daily');
    this.sidePanelSearch.set('');
    this.selectedMonthDay.set(null);
    this.currentDate.set(TimesheetService.startOfLocalDay(d));
  }

  /** Creates a new time entry with generated id. */
  addEntry(entry: Omit<TimesheetEntry, 'id'>): void {
    const id = `e${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    this.entries.update((arr) => [...arr, { ...entry, id }]);
  }

  /** Updates an existing entry by id with partial patch. */
  updateEntry(id: string, patch: Partial<TimesheetEntry>): void {
    this.entries.update((arr) => arr.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  }

  /** Drop placeholder rows for an unreported id without changing mapped flags (e.g. before saving map). */
  discardEntriesByUnreportedId(unrId: string): void {
    this.entries.update((arr) => arr.filter((e) => e.unrId !== unrId));
  }

  /** Deletes one entry and unmaps its unreported source if linked. */
  deleteEntry(id: string): void {
    const hit = this.entries().find((e) => e.id === id);
    if (hit?.unrId) this.markMapped(hit.unrId, false);
    this.entries.update((arr) => arr.filter((e) => e.id !== id));
  }

  /** Deletes multiple row entries and unmaps all linked unreported items. */
  deleteRowEntries(ids: string[]): void {
    const idSet = new Set(ids);
    this.entries()
      .filter((e) => idSet.has(e.id) && e.unrId)
      .forEach((e) => this.markMapped(e.unrId!, false));
    this.entries.update((arr) => arr.filter((e) => !idSet.has(e.id)));
  }

  /**
   * API timesheet-data expands one `activity` with multiple `tasks[]` into one entry per task.
   * Map/edit merges rows by `timesheetActivityId`, or by `activityIndex` + project + date + type when id is absent.
   */
  entriesSharingActivity(entry: TimesheetEntry): TimesheetEntry[] {
    const pid = entry.timesheetProjectId;
    const dateStr = entry.date;
    const at = entry.activityType;
    if (pid == null || !at || !entry.id.startsWith(API_ENTRY_ID_PREFIX)) {
      return [entry];
    }
    const aid = entry.timesheetActivityId;
    if (aid != null && aid > 0) {
      const matches = this.entries().filter(
        (e) =>
          e.timesheetActivityId === aid &&
          e.timesheetProjectId === pid &&
          e.date === dateStr &&
          e.activityType === at &&
          e.id.startsWith(API_ENTRY_ID_PREFIX),
      );
      return matches.sort(sortEntriesSharingActivity);
    }
    const ix = entry.activityIndex;
    if (ix != null && ix >= 0) {
      const matches = this.entries().filter(
        (e) =>
          e.activityIndex === ix &&
          e.timesheetProjectId === pid &&
          e.date === dateStr &&
          e.activityType === at &&
          e.id.startsWith(API_ENTRY_ID_PREFIX),
      );
      return matches.sort(sortEntriesSharingActivity);
    }
    return [entry];
  }

  /** Updates only comment text for an entry. */
  updateComment(id: string, comment: string): void {
    this.updateEntry(id, { comment });
  }

  /** Calls backend DELETE /project-timesheet/task/:taskId/activity/:activityId for each mapped task. */
  async deleteMappedActivityTasks(
    taskIds: number[],
    activityId: number | null,
    payload?: DeleteMappedActivityTasksPayload,
  ): Promise<void> {
    const safeActivityId = Number(activityId);
    const safeTaskIds = Array.from(
      new Set(taskIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)),
    );
    if (safeTaskIds.length === 0) return;
    const activityIdForApi = Number.isFinite(safeActivityId) ? Math.trunc(safeActivityId) : undefined;
    const shouldSendActivityId = (payload?.sourceType ?? '').toLowerCase() !== 'manual';
    await firstValueFrom(
      forkJoin(
        safeTaskIds.map((taskId) => {
          return this.timesheetManagementApi
            .deleteProjectTimesheetTaskActivity(
              taskId,
              shouldSendActivityId ? activityIdForApi : undefined,
              payload,
            )
            .pipe(
              take(1),
              // Keep local delete flow resilient even when one API delete fails.
              catchError(() => of(null)),
            );
        }),
      ),
    );
  }

  /** Submits the current week for approval and logs history event. */
  submitWeek(): boolean {
    if (!this.canSubmitOrResubmitCurrentWeek()) return false;
    const wk = this.currentWeekKey();
    this.weekStatus.update((s) => ({ ...s, [wk]: 'submitted' }));
    const ev: ApprovalHistoryEvent = { type: 'submitted', actor: 'Arjun Kapoor', role: 'Architect', date: this.dateToString(this.today()), note: null };
    this.approvedHistory.update((h) => ({ ...h, [wk]: [...(h[wk] ?? []), ev] }));
    return true;
  }

  /** Returns week status for a given reference date. */
  getWeekStatus(ref: Date): WeekStatus {
    return this.weekStatus()[this.getWeekKey(ref)] ?? 'draft';
  }

  /** True when API project-day status blocks edits (pending/submitted or approved). */
  isProjectDaySubmissionLocked(status: WeekStatus | undefined): boolean {
    return status === 'submitted' || status === 'approved';
  }

  /** True when a weekly project group has pending approval (submitted rollup). */
  projectHasPendingSubmission(pg: WeeklyProjectGroup): boolean {
    return (pg.weekSubmissionStatus ?? 'draft') === 'submitted';
  }

  /** True when a daily project group is pending approval for that day. */
  dailyProjectHasPendingSubmission(pg: DailyProjectGroup): boolean {
    const st = pg.submissionStatus ?? 'draft';
    const raw = (pg.submissionStatusRaw ?? '').trim().toLowerCase();
    return st === 'submitted' || raw === 'pending' || raw === 'submitted_for_approval';
  }

  /** Resolves API `project_id` from a weekly project group’s entries. */
  resolveWeeklyProjectGroupId(pg: WeeklyProjectGroup): number | null {
    for (const task of pg.tasks) {
      for (const list of Object.values(task.entriesByDate)) {
        for (const e of list) {
          const n = Number(e.timesheetProjectId);
          if (Number.isFinite(n) && n > 0) return Math.trunc(n);
        }
      }
    }
    return null;
  }

  /** Resolves API `project_id` from a daily project group’s entries. */
  resolveDailyProjectGroupId(pg: DailyProjectGroup): number | null {
    for (const activity of pg.activities) {
      for (const row of activity.rows) {
        const n = Number(row.timesheetProjectId);
        if (Number.isFinite(n) && n > 0) return Math.trunc(n);
      }
    }
    for (const task of pg.tasks) {
      for (const entry of task.entries) {
        const n = Number(entry.timesheetProjectId);
        if (Number.isFinite(n) && n > 0) return Math.trunc(n);
      }
    }
    return null;
  }

  /**
   * Collects per-project status for a calendar day from API rows + local entries.
   */
  private projectDayStatusesForDate(dateStr: string): Map<number, WeekStatus> {
    const byPid = new Map<number, WeekStatus>();

    const apiRows = this.timesheetByDate()[dateStr];
    if (Array.isArray(apiRows)) {
      for (const p of apiRows) {
        const id = Number(p.project_id);
        if (!Number.isFinite(id) || id <= 0) continue;
        const st = normalizeApiProjectDayStatus(p.status) ?? 'draft';
        const prev = byPid.get(id);
        byPid.set(id, prev == null ? st : pickStricterProjectDayStatus(prev, st));
      }
    }

    for (const e of this.entries()) {
      if (e.date !== dateStr || e.timesheetProjectId == null) continue;
      const id = Number(e.timesheetProjectId);
      if (!Number.isFinite(id) || id <= 0) continue;
      const st: WeekStatus = e.projectDayStatus ?? 'draft';
      const prev = byPid.get(id);
      byPid.set(id, prev == null ? st : pickStricterProjectDayStatus(prev, st));
    }

    return byPid;
  }

  /** True when this calendar day has at least one CRM project in rejected status. */
  dateHasRejectedProject(dateStr: string): boolean {
    for (const st of this.projectDayStatusesForDate(dateStr).values()) {
      if (st === 'rejected') return true;
    }
    return false;
  }

  /**
   * Hide Add Entry when the week/day is pending or approved — unless any project is
   * rejected (including week-status fallback after rejected rows were deleted).
   * Backend scopes the Add Entry project dropdown to rejected projects.
   */
  shouldHideAddEntryForDate(dateStr: string): boolean {
    if (this.dateHasRejectedProject(dateStr) || this.hasRejectedProjectInCurrentWeek()) {
      return false;
    }
    if (this.isSelectedWeekLockedForNewEntries()) return true;
    const byPid = this.projectDayStatusesForDate(dateStr);
    if (byPid.size === 0) return false;
    for (const st of byPid.values()) {
      if (st === 'approved' || st === 'submitted') return true;
    }
    return false;
  }

  /**
   * True when the given calendar day is locked for unreported mapping / full-day edits:
   * week is pending/approved, or every CRM project on that day is submitted/approved.
   */
  isDateLockedForSubmissionEdits(dateStr: string): boolean {
    if (this.isSelectedWeekLockedForNewEntries()) return true;
    const byPid = this.projectDayStatusesForDate(dateStr);
    if (byPid.size === 0) return false;
    return [...byPid.values()].every((st) => this.isProjectDaySubmissionLocked(st));
  }

  /**
   * Add Entry allowed when the week/day is draft, or any project is rejected
   * (rejected unlocks add so users can re-log after delete; dropdown is BE-filtered).
   */
  dailyViewAllowsAddEntry(entries: TimesheetEntry[]): boolean {
    const dateStr = entries[0]?.date;
    if (dateStr) return !this.shouldHideAddEntryForDate(dateStr);
    if (this.hasRejectedProjectInCurrentWeek()) return true;
    if (this.isSelectedWeekLockedForNewEntries()) return false;
    if (entries.length === 0) return true;
    for (const e of entries) {
      const st = e.projectDayStatus ?? 'draft';
      if (st === 'approved' || st === 'submitted') return false;
    }
    return true;
  }

  /** Returns approval history list for selected week. */
  getHistoryForWeek(ref: Date): ApprovalHistoryEvent[] {
    return this.approvedHistory()[this.getWeekKey(ref)] ?? [];
  }

  /** Marks an unreported activity as mapped/unmapped. */
  markMapped(unreportedId: string, mapped: boolean): void {
    this.unreported.update((arr) => arr.map((u) => (u.id === unreportedId ? { ...u, mapped } : u)));
  }

  /** Converts Date to YYYY-MM-DD local string. */
  dateToString(date: Date): string {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0];
  }

  /** Returns Monday-Sunday dates for a given reference date. */
  getWeekDays(ref: Date): Date[] {
    const d = new Date(ref);
    const dow = d.getDay();
    const diff = dow === 0 ? -6 : 1 - dow;
    const mon = new Date(d);
    mon.setDate(d.getDate() + diff);
    return Array.from({ length: 7 }).map((_, i) => {
      const x = new Date(mon);
      x.setDate(mon.getDate() + i);
      return x;
    });
  }

  /** Returns week key using Monday date as key. */
  getWeekKey(ref: Date): string {
    return this.dateToString(this.getWeekDays(ref)[0]);
  }

  /** Formats YYYY-MM-DD date for display. */
  formatDate(dateStr: string, options: Intl.DateTimeFormatOptions): string {
    return new Date(`${dateStr}T12:00:00`).toLocaleDateString('en-US', options);
  }

  /** Month grid rows (Mon–Sun); `null` = pad cell outside current month. */
  buildMonthWeeks(ref: Date): (Date | null)[][] {
    const y = ref.getFullYear();
    const m = ref.getMonth();
    const lastDay = new Date(y, m + 1, 0).getDate();
    const first = new Date(y, m, 1);
    const lead = (first.getDay() + 6) % 7;
    const cells: (Date | null)[] = [];
    for (let i = 0; i < lead; i += 1) cells.push(null);
    for (let d = 1; d <= lastDay; d += 1) cells.push(new Date(y, m, d));
    while (cells.length % 7 !== 0) cells.push(null);
    const weeks: (Date | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
  }

  /** Projects for a calendar day (month hover popover); prefers API `by_date`, else entries rollup. */
  getDayProjectDetails(dateStr: string): MonthDayProjectDetail[] {
    const apiRows = this.timesheetByDate()[dateStr];
    if (apiRows?.length) return mapApiDayProjectsToDetails(apiRows);
    return deriveDayProjectsFromEntries(this.entries().filter((e) => e.date === dateStr));
  }

  /** Aggregates entries for a calendar day (month cell). */
  dayRollup(dateStr: string): { hours: number; screen: number; entryCount: number } {
    const es = this.entries().filter((e) => e.date === dateStr);
    return {
      hours: es.reduce((s, e) => s + e.hrs, 0),
      screen: es.reduce((s, e) => s + e.screen, 0),
      entryCount: es.length,
    };
  }

  isRealToday(d: Date): boolean {
    return this.dateToString(d) === this.dateToString(this.today());
  }

  isFutureCalendarDay(d: Date): boolean {
    return this.dateToString(d) > this.dateToString(this.today());
  }

  /**
   * Earliest local YYYY-MM-DD the signed-in user may add or update My Timesheet entries.
   * Later of the product fill floor and login `metaData.user.dateOfJoining`.
   */
  timesheetFillMinDateStr(): string {
    const doj = readDateOfJoiningFromUserDetailsCookie(this.cookieService.get('userDetails'));
    return resolveTimesheetFillMinDateStr(doj);
  }

  /** True when `dateStr` is before {@link timesheetFillMinDateStr}. */
  isBeforeTimesheetFillMinDate(dateStr: string): boolean {
    const day = (dateStr ?? '').trim().slice(0, 10);
    if (!day) return true;
    return day < this.timesheetFillMinDateStr();
  }

  /** User-facing guard copy when add/update is blocked by fill minimum / date of joining. */
  timesheetFillMinGuardMessage(): string {
    const min = this.timesheetFillMinDateStr();
    const doj = readDateOfJoiningFromUserDetailsCookie(this.cookieService.get('userDetails'));
    if (doj && doj === min) {
      return `Entries cannot be added or updated before your date of joining (${min}).`;
    }
    return `Entries cannot be added or updated before ${min}.`;
  }

  /** Sum unmapped screen hours for unreported rows on the given calendar days. */
  sumUnmappedUnreportedHours(dates: ReadonlySet<string>): number {
    if (dates.size === 0) return 0;
    return this.unreported()
      .filter((u) => !u.mapped && dates.has(u.date))
      .reduce((s, u) => s + u.hrs, 0);
  }

  /** Sum unmapped unreported hours for every day in the calendar month of `ref`. */
  sumUnmappedUnreportedHoursInMonth(ref: Date): number {
    const y = ref.getFullYear();
    const m = ref.getMonth();
    return this.unreported()
      .filter((u) => {
        if (u.mapped) return false;
        const d = new Date(`${u.date}T12:00:00`);
        return d.getFullYear() === y && d.getMonth() === m;
      })
      .reduce((s, u) => s + u.hrs, 0);
  }

  /** Builds four-card stats model for supplied entries. */
  computeStats(
    entries: TimesheetEntry[],
    unreportedHrs: number,
    options?: { statsScope?: 'daily' | 'weekOrMonth'; unreportedSub?: string },
  ): StatCard[] {
    const total = entries.reduce((s, e) => s + e.hrs, 0);
    const screen = entries.reduce((s, e) => s + e.screen, 0);
    const bill = entries.filter((e) => e.billable).reduce((s, e) => s + e.hrs, 0);
    const unreportedSub =
      options?.unreportedSub ?? (options?.statsScope === 'daily' ? 'Today' : 'This period');
    const loggedSub = options?.statsScope === 'daily' ? 'Today' : 'Total';
    return [
      { label: 'Logged Hours', value: total.toFixed(1), sub: loggedSub, icon: 'clock', bgColor: 'var(--blue-lt)', textColor: 'var(--blue)' },
      {
        label: 'Screen Hours',
        value: formatScreenHoursForDisplay(screen),
        sub: 'Detected',
        icon: 'monitor',
        bgColor: 'var(--purple-lt)',
        textColor: 'var(--purple)',
      },
      { label: 'Billable Hours', value: bill.toFixed(1), sub: 'Billable', icon: 'dollar', bgColor: 'var(--green-lt)', textColor: 'var(--green)' },
      {
        label: 'Unallocated Hours',
        value: unreportedHrs.toFixed(1),
        sub: unreportedSub,
        icon: 'zap',
        bgColor: 'var(--amber-lt)',
        textColor: 'var(--amber)',
      },
    ];
  }

  /** Groups entries by project and task for expandable daily rows. */
  groupDailyEntriesByProjectAndTask(entries: TimesheetEntry[]): DailyProjectGroup[] {
    const pMap = new Map<string, DailyProjectGroup>();
    for (const e of entries) {
      const pKey = `${e.proj}|${e.color}`;
      if (!pMap.has(pKey)) {
        pMap.set(pKey, {
          proj: e.proj,
          color: e.color,
          totalHrs: 0,
          activities: [],
          tasks: [],
          projectCode: e.projectCode,
          entryType: e.type,
        });
      }
      const pg = pMap.get(pKey)!;
      if (!pg.projectCode && e.projectCode) pg.projectCode = e.projectCode;
      if (pg.entryType == null) pg.entryType = e.type;
      if (e.projectDayStatus != null) {
        pg.submissionStatus =
          pg.submissionStatus == null
            ? e.projectDayStatus
            : mergeDailySubmissionStatus(pg.submissionStatus, e.projectDayStatus);
      }
      if (e.projectDayStatusRaw) {
        if (pg.submissionStatusRaw == null) pg.submissionStatusRaw = e.projectDayStatusRaw;
      }
      pg.totalHrs += e.hrs;
      const actKey = dailyActivityGroupKey(e);
      if (actKey) {
        let ag = pg.activities.find((x) => x.activityKey === actKey);
        if (!ag) {
          ag = {
            activityKey: actKey,
            activityType: e.activityType!,
            activityAppLabel: e.activityAppLabel,
            ...(e.timesheetActivityId != null && e.timesheetActivityId > 0
              ? { timesheetActivityId: e.timesheetActivityId }
              : {}),
            ...(e.activityIndex != null ? { activityIndex: e.activityIndex } : {}),
            rows: [],
            totalHrs: 0,
            totalScreen: 0,
          };
          pg.activities.push(ag);
        }
        if (!ag.activityAppLabel && e.activityAppLabel) ag.activityAppLabel = e.activityAppLabel;
        ag.rows.push(e);
        ag.totalHrs += e.hrs;
        ag.totalScreen += e.screen;
      } else {
        const taskKey = taskRollupGroupingKey(e);
        let t = pg.tasks.find((x) => x.taskKey === taskKey);
        if (!t) {
          t = {
            taskKey,
            taskName: e.task,
            stage: e.stage,
            ms: e.ms,
            ...(e.activityTaskLines?.[0]?.stageDescription
              ? { stageDescription: e.activityTaskLines[0].stageDescription }
              : {}),
            ...(e.activityTaskLines?.[0]?.milestoneDescription
              ? { msDescription: e.activityTaskLines[0].milestoneDescription }
              : {}),
            entries: [],
            totalHrs: 0,
            totalScreen: 0,
          };
          pg.tasks.push(t);
        }
        t.entries.push(e);
        t.totalHrs += e.hrs;
        t.totalScreen += e.screen;
      }
    }
    for (const pg of pMap.values()) {
      for (const ag of pg.activities) {
        ag.rows.sort((a, b) => {
          const ta = a.activityTaskLines?.[0]?.taskId ?? 0;
          const tb = b.activityTaskLines?.[0]?.taskId ?? 0;
          if (ta !== tb) return ta - tb;
          return a.id.localeCompare(b.id);
        });
      }
      pg.activities.sort((a, b) => {
        const aidA = a.timesheetActivityId ?? 0;
        const aidB = b.timesheetActivityId ?? 0;
        if (aidA !== aidB) return aidA - aidB;
        const ixA = a.activityIndex ?? 0;
        const ixB = b.activityIndex ?? 0;
        if (ixA !== ixB) return ixA - ixB;
        return a.activityType.localeCompare(b.activityType);
      });
      pg.tasks.sort((a, b) => a.taskName.localeCompare(b.taskName));
    }
    return [...pMap.values()];
  }

  /** Builds weekly project/task matrix model for table rendering. */
  buildWeeklyProjectGroups(entries: TimesheetEntry[]): WeeklyProjectGroup[] {
    const pMap = new Map<string, WeeklyProjectGroup>();
    for (const e of entries) {
      const pKey = `${e.proj}|${e.color}`;
      if (!pMap.has(pKey)) {
        pMap.set(pKey, {
          proj: e.proj,
          color: e.color,
          tasks: [],
          projectCode: e.projectCode,
          entryType: e.type,
        });
      }
      const pg = pMap.get(pKey)!;
      if (!pg.projectCode && e.projectCode) pg.projectCode = e.projectCode;
      if (pg.entryType == null) pg.entryType = e.type;
      const taskKey = taskRollupGroupingKey(e);
      let t = pg.tasks.find((x) => x.taskKey === taskKey);
      if (!t) {
        t = { taskKey, taskName: e.task, isAuto: false, entryIds: [], entriesByDate: {}, totalScreenHrs: 0, comments: [] };
        pg.tasks.push(t);
      }
      t.entryIds.push(e.id);
      const arr = t.entriesByDate[e.date] ?? [];
      arr.push(e);
      t.entriesByDate[e.date] = arr;
      t.totalScreenHrs += e.screen;
      if (e.auto) t.isAuto = true;
      if (e.comment) t.comments.push(e.comment);
    }
    for (const pg of pMap.values()) {
      pg.tasks.sort((a, b) => a.taskName.localeCompare(b.taskName));
      pg.weekSubmissionStatus = rollupProjectWeekStatusFromWeeklyTasks(pg.tasks);
      const all = [...pg.tasks];
      const kept: WeeklyTaskGroup[] = [];
      let supLogMinutes = 0;
      let supScrMinutes = 0;
      for (const t of all) {
        const sample = firstEntryInWeeklyTaskGroup(t);
        if (!sample || weeklyTaskRowHasDisplayLabel(t, sample)) {
          kept.push(t);
        } else {
          for (const list of Object.values(t.entriesByDate)) {
            for (const e of list) {
              supLogMinutes += wholeMinutesFromHours(e.hrs);
              supScrMinutes += wholeMinutesFromHours(e.screen);
            }
          }
        }
      }
      kept.sort((a, b) => a.taskName.localeCompare(b.taskName));
      pg.tasks = kept;
      if (supLogMinutes !== 0 || supScrMinutes !== 0) {
        pg.suppressedLoggedHrs = supLogMinutes / 60;
        pg.suppressedScreenHrs = supScrMinutes / 60;
      }
    }
    return [...pMap.values()];
  }
}